import { useMemo } from 'react';
import { allocationLabel, colorForAllocationRow } from '../lib/allocationColors';
import type { NetWorthSnapshot } from '../types';
import { formatEuro } from '../utils/formatters';
import styles from './AllocationTrendChart.module.css';

type Props = {
  snapshots: NetWorthSnapshot[];
  loading?: boolean;
  error?: string | null;
};

const CHART_WIDTH = 420;
const CHART_HEIGHT = 180;
const MARGIN = { top: 12, right: 12, bottom: 24, left: 48 };
const GRID_LINE_COUNT = 3;

function formatShortDate(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

function formatCompactEuro(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} M €`;
  }
  if (abs >= 10_000) {
    return `${(value / 1_000).toLocaleString('de-DE', { maximumFractionDigits: 0 })} k €`;
  }
  return formatEuro(value);
}

type AllocationPoint = {
  date: string;
  label: string;
  amount: number;
  x: number;
  y: number;
};

type AllocationSeries = {
  id: string;
  label: string;
  color: string;
  latest: number;
  points: AllocationPoint[];
  linePath: string;
};

function snapshotsWithAllocation(snapshots: NetWorthSnapshot[]): NetWorthSnapshot[] {
  return snapshots.filter(
    (snapshot) => snapshot.allocation && Object.keys(snapshot.allocation).length > 0,
  );
}

function orderedCategoryIds(snapshots: NetWorthSnapshot[]): string[] {
  const latest = snapshots[snapshots.length - 1]?.allocation ?? {};
  const ids = new Set<string>();
  for (const snapshot of snapshots) {
    for (const id of Object.keys(snapshot.allocation ?? {})) ids.add(id);
  }
  return Array.from(ids).sort((a, b) => (latest[b] ?? 0) - (latest[a] ?? 0));
}

function buildGeometry(snapshots: NetWorthSnapshot[]) {
  const innerWidth = CHART_WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;
  const categoryIds = orderedCategoryIds(snapshots);

  const allValues: number[] = [];
  for (const snapshot of snapshots) {
    for (const id of categoryIds) {
      allValues.push(snapshot.allocation?.[id] ?? 0);
    }
  }

  const minValue = Math.min(0, ...allValues);
  const maxValue = Math.max(...allValues, 1);
  const span = maxValue - minValue;
  const padding = span > 0 ? span * 0.1 : Math.max(maxValue * 0.05, 1000);
  const yMin = minValue;
  const yMax = maxValue + padding;
  const yRange = yMax - yMin || 1;

  const xScale = (index: number) =>
    MARGIN.left + (index / Math.max(snapshots.length - 1, 1)) * innerWidth;
  const yScale = (value: number) => MARGIN.top + ((yMax - value) / yRange) * innerHeight;

  const series: AllocationSeries[] = categoryIds.map((id, index) => {
    const points: AllocationPoint[] = snapshots.map((snapshot, i) => {
      const amount = snapshot.allocation?.[id] ?? 0;
      return {
        date: snapshot.snapshotDate,
        label: formatShortDate(snapshot.snapshotDate),
        amount,
        x: xScale(i),
        y: yScale(amount),
      };
    });
    const linePath = points
      .map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
      .join(' ');
    return {
      id,
      label: allocationLabel(id),
      color: colorForAllocationRow(id, index),
      latest: snapshots[snapshots.length - 1]?.allocation?.[id] ?? 0,
      points,
      linePath,
    };
  });

  const horizontalGridLines = Array.from({ length: GRID_LINE_COUNT }, (_, index) => {
    const ratio = index / (GRID_LINE_COUNT - 1);
    const value = yMax - ratio * yRange;
    return {
      y: yScale(value),
      label: formatCompactEuro(value),
      isBaseline: index === GRID_LINE_COUNT - 1,
    };
  });

  return { series, innerHeight, horizontalGridLines, baselineY: MARGIN.top + innerHeight };
}

export default function AllocationTrendChart({ snapshots, loading = false, error = null }: Props) {
  const usable = useMemo(() => snapshotsWithAllocation(snapshots), [snapshots]);
  const geometry = useMemo(
    () => (usable.length >= 2 ? buildGeometry(usable) : null),
    [usable],
  );

  if (loading) {
    return (
      <div className={styles.wrap} aria-busy="true">
        <p className={styles.hint}>Loading history…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.wrap}>
        <p className={styles.hint}>{error}</p>
      </div>
    );
  }

  if (!geometry) {
    return (
      <div className={styles.wrap}>
        <p className={styles.hint}>
          Allocation history starts building once two daily snapshots are captured. Check back
          tomorrow.
        </p>
      </div>
    );
  }

  const first = usable[0];
  const last = usable[usable.length - 1];
  const chartSummary = `Allocation by asset type from ${formatShortDate(first.snapshotDate)} to ${formatShortDate(last.snapshotDate)}.`;

  return (
    <figure className={styles.chart}>
      <div className={styles.chartPanel}>
        <svg
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          className={styles.svg}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={chartSummary}
        >
          <g className={styles.gridGroup} aria-hidden="true">
            {geometry.horizontalGridLines.map((gridLine) => (
              <g key={gridLine.label}>
                <line
                  x1={MARGIN.left}
                  x2={CHART_WIDTH - MARGIN.right}
                  y1={gridLine.y}
                  y2={gridLine.y}
                  className={gridLine.isBaseline ? styles.gridLineBaseline : styles.gridLine}
                />
                <text
                  x={MARGIN.left - 6}
                  y={gridLine.y + 3}
                  className={styles.yAxisLabel}
                  textAnchor="end"
                >
                  {gridLine.label}
                </text>
              </g>
            ))}
          </g>

          {geometry.series.map((line) => (
            <g key={line.id}>
              <path
                d={line.linePath}
                className={styles.line}
                style={{ stroke: line.color }}
                pathLength={1}
                aria-hidden="true"
              />
              {(() => {
                const point = line.points[line.points.length - 1];
                return (
                  <circle cx={point.x} cy={point.y} r={2.5} fill={line.color} aria-hidden="true">
                    <title>{`${line.label}: ${formatEuro(line.latest)}`}</title>
                  </circle>
                );
              })()}
            </g>
          ))}

          <text
            x={MARGIN.left}
            y={CHART_HEIGHT - 6}
            className={styles.axisLabel}
            textAnchor="start"
          >
            {formatShortDate(first.snapshotDate)}
          </text>
          <text
            x={CHART_WIDTH - MARGIN.right}
            y={CHART_HEIGHT - 6}
            className={styles.axisLabel}
            textAnchor="end"
          >
            {formatShortDate(last.snapshotDate)}
          </text>
        </svg>
      </div>

      <ul className={styles.legend} aria-label="Asset types">
        {geometry.series.map((line) => (
          <li key={line.id} className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{ backgroundColor: line.color }}
              aria-hidden="true"
            />
            <span className={styles.legendLabel}>{line.label}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}
